import { NgModule, CUSTOM_ELEMENTS_SCHEMA, InjectionToken } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HistoryComponent } from './pages/history/history.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AgGridModule } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import {
  MsalService,
  MSAL_INSTANCE,
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
} from './msal';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
} from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from './msal/constants';
import { MsalInterceptorConfig } from './msal/msal.interceptor.config';
import { AddResolutionComponent } from './core/check/add-resolution/add-resolution.component';
import { environment } from 'src/environments/environment';
import { ConstantsService } from './services/constants.service';
import { Constants } from './constants';
import { SearchImgCellRendererComponent } from './core/grid-renderer/search-img-cell-renderer/search-img-cell-renderer.component';
import { PopupComponent } from './core/check/popup/popup.component';
import { BreadcrumbComponent } from './core/breadcrumb/breadcrumb.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RefreshComponent } from './core/grid-renderer/refresh/refresh.component';
import { ColumnOptionsComponent } from './pages/history/column-options/column-options.component';
import { LegendsComponent } from './pages/history/legends/legends.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ViewEventDeletionsComponent } from './pages/history/view-event-deletions/view-event-deletions.component';
import { CreateEventComponent } from './pages/create-event/create-event.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { CellsComponent } from './core/grid-renderer/cells/cells.component';
import { EventDetailsComponent } from './pages/event-details/event-details.component';
import { EventComponent } from './pages/event/event.component';
import { ConnectionsComponent } from './pages/connections/connections.component';
import { ResolutionsComponent } from './pages/resolutions/resolutions.component';
import { ResolutionOrdersComponent } from './pages/resolution-orders/resolution-orders.component';
import { EventHistoryComponent } from './pages/event-history/event-history.component';
import { ReportsDashboardComponent } from './pages/reports-dashboard/reports-dashboard.component';
import { ReportsOrderInfoComponent } from './pages/reports-order-info/reports-order-info.component';
import { ReportsImportComponent } from './pages/reports-import/reports-import.component';
import { ReportsHistoryComponent } from './pages/reports-history/reports-history.component';
import { ReportsSafetyDashboardComponent } from './pages/reports-safety-dashboard/reports-safety-dashboard.component';
import { ReportsToolbarComponent } from './pages/reports-toolbar/reports-toolbar.component';
import { ImportFtpFileImportComponent } from './pages/import-ftp-file-import/import-ftp-file-import.component';
import { LogImgCellRenderComponent } from './core/grid-renderer/log-img-cell-render/log-img-cell-render.component';
import { FileUploadComponent } from './pages/file-upload/file-upload.component';
import { JobLogComponent } from './core/check/job-log/job-log.component';
import { NgxUiLoaderConfig, NgxUiLoaderModule } from 'ngx-ui-loader';
import { DashboardComponent, PreventContextMenuInGrid } from './pages/dashboard/dashboard.component';
import { ButtonRendererComponent } from './core/grid-renderer/button-renderer/button-renderer.component';
import { SettingsRendererComponent } from './core/grid-renderer/settings-renderer/settings-renderer.component';
import { RefreshRendererComponent } from './core/grid-renderer/refresh-renderer/refresh-renderer.component';
import { FileExportRendererComponent } from './core/grid-renderer/file-export-renderer/file-export-renderer.component';
import { ToolbarComponent } from './core/toolbar/toolbar.component';
import { ControlPointMatrixComponent } from './pages/control-point-matrix/control-point-matrix.component';
import { ViewCommentsComponent } from './core/check/view-comments/view-comments.component';
import { UpdateCriteriaComponent } from './core/check/update-criteria/update-criteria.component';
import { UpdateKpiComponent } from './core/check/update-kpi/update-kpi.component';
import { NgChartsModule } from 'ng2-charts';
import { ngxUiLoaderConfig } from './core/constant/spinner.constant';
import { HistoryContentsComponent } from './pages/history/history-contents/history-contents.component';
import { ImportLogComponent } from './core/check/import-log/import-log/import-log.component';
import { ResolutionBaseComponent } from './pages/resolutions/resolution-base/resolution-base.component';
import { NotificationMessageComponent } from './core/notification-message/notification-message.component';
import { CreateEventPopupComponent } from './core/check/create-event-popup/create-event-popup.component';
import { FinalEditSafetyComponent } from './core/check/final-edit-safety/final-edit-safety.component';
import { EditSafetyComponent } from './core/check/edit-safety/edit-safety.component';
import { ImagePopUpComponent } from './core/check/image-pop-up/image-pop-up.component';
import { SafetyDashboardComponent } from './pages/safety-dashboard/safety-dashboard.component';
import { CustomDetailComponentComponent } from './core/check/custom-detail-component/custom-detail-component.component';
import { CloseEventComponent } from './core/check/close-event/close-event.component';
import { SafetyDashboardBaseComponent } from './pages/safety-dashboard/safety-dashboard-base/safety-dashboard-base.component';
import { ChildGridComponent } from './pages/safety-dashboard/child-grid/child-grid.component';
import { FooterComponent } from './core/footer/footer.component';
import { DashboardBaseComponent } from './pages/dashboard/dashboard-base/dashboard-base.component';
import { MasterConfigComponent } from './pages/dashboard/master-config/master-config.component';
import { MasterActionRendererComponent } from './core/grid-renderer/master-action-renderer/master-action-renderer.component';
import { UpdateMasterSettingsComponent } from './core/check/update-master-settings/update-master-settings.component';
import { RefreshMasterSettingsComponent } from './core/check/refresh-master-settings/refresh-master-settings.component';
import { MasterSettingsRendererComponent } from './core/grid-renderer/master-settings-renderer/master-settings-renderer.component';
import { DashboardSettingsComponent } from './core/check/dashboard-settings/dashboard-settings.component';
import { HeaderComponent } from './core/header/header.component';
import { LiosNavbarComponent } from './core/lios-navbar/lios-navbar.component';
import { KpiComponent } from './pages/kpi/kpi.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { EditBtnRendererComponent } from './core/grid-renderer/edit-btn-renderer/edit-btn-renderer.component';
import { CustomFilterComponent } from './pages/import-ftp-file-import/custom-filter.component';
import { EditEventComponent } from './core/check/edit-event/edit-event.component';
import { EditButtonComponent } from './core/grid-renderer/edit-button/edit-button.component';
import { AddEventButtonComponent } from './pages/dashboard/add-event-button/add-event-button.component';
import { EditResolutionComponent } from './core/check/edit-resolution/edit-resolution.component';
import { DeletePopupComponent } from './core/check/delete-popup/delete-popup.component';
import { AddResolutionOrdersComponent } from './pages/add-resolution-orders/add-resolution-orders.component';
import { AddresolutionordersgridComponent } from './pages/addresolutionordersgrid/addresolutionordersgrid.component';
import { AddResolutionOrdersPopupComponent } from './core/check/add-resolution-orders-popup/add-resolution-orders-popup.component';
import { AddBtnRendererComponent } from './core/grid-renderer/add-btn-renderer/add-btn-renderer.component';
import { OrderNumberComponent } from './core/grid-renderer/order-number/order-number.component';
import { DisplayOrdersComponent } from './core/check/display-orders/display-orders.component';
import { CustomHeaderComponent } from './core/grid-renderer/custom-header/custom-header.component';
import { CloseStatusComponent } from './core/check/close-status/close-status.component';
import { BorderCrossingComponent } from './core/check/border-crossing/border-crossing.component';
import { DateTimeEditor } from './core/check/border-crossing/datetime-editor/datetime-editor.component';
import { ReportsBorderCrossing } from './pages/reports-border-crossing/reports-border-crossing.component';
import { EventEditHistoryComponent } from './pages/event-edit-history/event-edit-history.component';
import { DelayDispatchComponent } from './pages/delay-dispatch/delay-dispatch.component';
import { NotificationComponent } from './pages/notification/notification.component';
import { CriteriaComponent } from './pages/notification/criteria/criteria.component';
import { NotificationDialogComponent } from './pages/notification/notification-dialog/notification-dialog.component';
import { RemoteGridBindingDirective } from './pages/notification/ag-table.directive';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { APP_INITIALIZER, inject, provideAppInitializer } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/cache';
import {
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
} from '@azure/msal-angular';

import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { ContextMenuModule, MasterDetailModule } from 'ag-grid-enterprise';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AddParamsInterceptor } from './add-params.interceptor';
import { HttpErrorInterceptor } from './http-error.interceptor';
import { FgaModule } from './fga/fga.module';

ModuleRegistry.registerModules([
  AllCommunityModule,
  MasterDetailModule,
  ContextMenuModule
])
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.CLIENT_ID,
      authority: environment.TENANT_ID,
      redirectUri: environment.REDIRECT_URL
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.REDIRECT_URL,
    environment.SCOPES
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.SCOPES],
    },
    loginFailedRoute: '/unauthorized',
  };
}
export function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
  return () => instance.initialize();
}
export const FGA_API_ENDPOINT = new InjectionToken<string>('FGA_API_ENDPOINT');
@NgModule({
  declarations: [
    AppComponent,
    ChildGridComponent,
    AddResolutionComponent,
    FooterComponent,
    CloseEventComponent,
    CustomDetailComponentComponent,
    SafetyDashboardComponent,
    FinalEditSafetyComponent,
    ImagePopUpComponent,
    EditSafetyComponent,
    HistoryComponent,
    SearchImgCellRendererComponent,
    PopupComponent,
    BreadcrumbComponent,
    RefreshComponent,
    ColumnOptionsComponent,
    LegendsComponent,
    ViewEventDeletionsComponent,
    CreateEventComponent,
    CellsComponent,
    EventDetailsComponent,
    EventComponent,
    ConnectionsComponent,
    ResolutionsComponent,
    ResolutionOrdersComponent,
    EventHistoryComponent,
    ReportsDashboardComponent,
    ReportsOrderInfoComponent,
    ReportsImportComponent,
    ReportsHistoryComponent,
    ReportsSafetyDashboardComponent,
    ReportsToolbarComponent,
    ImportFtpFileImportComponent,
    LogImgCellRenderComponent,
    BreadcrumbComponent,
    FileUploadComponent,
    JobLogComponent,
    DashboardComponent,
    ButtonRendererComponent,
    SettingsRendererComponent,
    RefreshRendererComponent,
    FileExportRendererComponent,
    ToolbarComponent,
    ControlPointMatrixComponent,
    ViewCommentsComponent,
    UpdateCriteriaComponent,
    UpdateKpiComponent,
    HistoryContentsComponent,
    ImportLogComponent,
    ResolutionBaseComponent,
    NotificationMessageComponent,
    CreateEventPopupComponent,
    SafetyDashboardBaseComponent,
    HeaderComponent,
    LiosNavbarComponent,
    KpiComponent,
    DashboardBaseComponent,
    MasterConfigComponent,
    MasterActionRendererComponent,
    UpdateMasterSettingsComponent,
    RefreshMasterSettingsComponent,
    MasterSettingsRendererComponent,
    DashboardSettingsComponent,
    EditBtnRendererComponent,
    CustomFilterComponent,
    EditEventComponent,
    EditButtonComponent,
    AddEventButtonComponent,
    EditResolutionComponent,
    DeletePopupComponent,
    AddResolutionOrdersComponent,
    AddresolutionordersgridComponent,
    AddResolutionOrdersPopupComponent,
    AddBtnRendererComponent,
    OrderNumberComponent,
    DisplayOrdersComponent,
    CustomHeaderComponent,
    CloseStatusComponent,
    BorderCrossingComponent,
    DateTimeEditor,
    PreventContextMenuInGrid,
    ReportsBorderCrossing,
    EventEditHistoryComponent,
    DelayDispatchComponent,
    NotificationComponent,
    CriteriaComponent,
    NotificationDialogComponent,
    RemoteGridBindingDirective,
    UnauthorizedComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent], imports: [
    BrowserModule,
    NgChartsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    AgGridModule,
    MatIconModule,
    MatListModule,
    MatDialogModule,
    MatMenuModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    MatExpansionModule,
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    MatSelectModule,
    NgxMaterialTimepickerModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    NgChartsModule,
    NgxMatSelectSearchModule,
    FgaModule,
    MatChipsModule], providers: [
      ConstantsService,
      Constants,
      DatePipe,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AddParamsInterceptor,
        multi: true
      },
      {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
      },
      provideAppInitializer(() => {
        const msalInstance = inject(MSAL_INSTANCE);
        return msalInstance.initialize();
      }),
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      provideHttpClient(withInterceptorsFromDi()),
      provideApollo(() => {
        return { cache: new InMemoryCache() }
      }),

      { provide: FGA_API_ENDPOINT, useValue: environment.APM_DS_URI }
    ]
})
export class AppModule { }
